import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Inbox,
  CalendarCheck,
  RefreshCw,
  Mail,
  Phone,
  CheckCircle2,
  Circle,
  Clock,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { authHeaders, getToken, getEmail } from "@/lib/auth";
import AdminNav from "@/components/AdminNav";

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [contacts, setContacts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeader = authHeaders;

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        api.get("/admin/contacts", { headers: authHeader() }),
        api.get("/admin/appointments", { headers: authHeader() }),
      ]);
      setContacts(c.data || []);
      setAppointments(a.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("ipg_admin_token");
        localStorage.removeItem("ipg_admin_email");
        toast.error("Session expired. Please log in again.");
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    setEmail(getEmail() || "");
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("ipg_admin_token");
    localStorage.removeItem("ipg_admin_email");
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  const toggleContactHandled = async (c) => {
    try {
      await api.patch(`/admin/contacts/${c.id}`, { handled: !c.handled }, { headers: authHeader() });
      setContacts((prev) => prev.map((x) => (x.id === c.id ? { ...x, handled: !c.handled } : x)));
    } catch {
      toast.error("Update failed.");
    }
  };

  const setApptStatus = async (a, status) => {
    try {
      await api.patch(`/admin/appointments/${a.id}`, { status }, { headers: authHeader() });
      setAppointments((prev) => prev.map((x) => (x.id === a.id ? { ...x, status } : x)));
    } catch {
      toast.error("Update failed.");
    }
  };

  const newContacts = contacts.filter((c) => !c.handled).length;
  const pendingAppts = appointments.filter((a) => a.status === "pending").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F8FAFC] pb-24">
      <AdminNav />
      <div className="px-6 md:px-12 lg:px-24 pt-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <p className="uppercase text-xs tracking-[0.25em] font-semibold text-[#0284C7]">
            Inbox · Owner Panel
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-slate-900 mt-3">
            Dashboard
          </h1>
          <p className="text-slate-600 mt-3 text-sm">Signed in as <span className="text-slate-900 font-medium">{email}</span></p>
        </div>
        <div className="flex gap-3">
          <Button
            data-testid="admin-refresh"
            variant="outline"
            onClick={fetchAll}
            disabled={loading}
            className="rounded-full h-11 border-slate-300 hover:bg-white"
          >
            <RefreshCw size={16} strokeWidth={1.5} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
        <StatCard icon={Inbox} label="Total Contacts" value={contacts.length} sub={`${newContacts} new`} />
        <StatCard icon={CalendarCheck} label="Total Appointments" value={appointments.length} sub={`${pendingAppts} pending`} />
        <StatCard icon={Clock} label="Last 7 Days" value={contacts.filter(isLast7).length + appointments.filter(isLast7).length} sub="combined" />
      </div>

      <div className="rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-[0_8px_30px_rgba(10,37,64,0.04)]">
        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="bg-slate-100 rounded-full h-12 p-1 mb-6">
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-full px-5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Contacts ({contacts.length})
            </TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments" className="rounded-full px-5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
              Appointments ({appointments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contacts">
            {loading ? (
              <EmptyState label="Loading..." />
            ) : contacts.length === 0 ? (
              <EmptyState label="No contact enquiries yet." />
            ) : (
              <div className="space-y-3">
                {contacts.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`contact-row-${c.id}`}
                    className="rounded-2xl border border-slate-100 p-5 hover:border-slate-200 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <p className="font-heading text-lg font-medium text-slate-900">{c.name}</p>
                          {c.handled ? (
                            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                              Handled
                            </span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                              New
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600 mt-2">
                          <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-slate-900">
                            <Mail size={14} strokeWidth={1.5} /> {c.email}
                          </a>
                          {c.phone && (
                            <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:text-slate-900">
                              <Phone size={14} strokeWidth={1.5} /> {c.phone}
                            </a>
                          )}
                        </div>
                        {c.subject && (
                          <p className="text-sm text-slate-500 mt-2"><span className="text-slate-400">Subject:</span> {c.subject}</p>
                        )}
                        <p className="text-sm text-slate-700 mt-3 leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-xl p-3 border border-slate-100">
                          {c.message}
                        </p>
                        <p className="text-xs text-slate-400 mt-3">{fmtDate(c.created_at)}</p>
                      </div>
                      <button
                        data-testid={`toggle-handled-${c.id}`}
                        onClick={() => toggleContactHandled(c)}
                        className="inline-flex items-center gap-2 self-start px-4 h-10 rounded-full border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        {c.handled ? <CheckCircle2 size={16} strokeWidth={1.5} className="text-emerald-600" /> : <Circle size={16} strokeWidth={1.5} />}
                        {c.handled ? "Handled" : "Mark Handled"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="appointments">
            {loading ? (
              <EmptyState label="Loading..." />
            ) : appointments.length === 0 ? (
              <EmptyState label="No appointments yet." />
            ) : (
              <div className="space-y-3">
                {appointments.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`appointment-row-${a.id}`}
                    className="rounded-2xl border border-slate-100 p-5 hover:border-slate-200 transition-colors"
                  >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <p className="font-heading text-lg font-medium text-slate-900">{a.name}</p>
                          <StatusPill status={a.status} />
                          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#0A2540] bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                            {a.division}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600 mt-2">
                          <a href={`mailto:${a.email}`} className="inline-flex items-center gap-1.5 hover:text-slate-900">
                            <Mail size={14} strokeWidth={1.5} /> {a.email}
                          </a>
                          <a href={`tel:${a.phone}`} className="inline-flex items-center gap-1.5 hover:text-slate-900">
                            <Phone size={14} strokeWidth={1.5} /> {a.phone}
                          </a>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 mt-3 text-sm">
                          <p className="text-slate-500">Date: <span className="text-slate-800">{a.preferred_date}</span></p>
                          <p className="text-slate-500">Time: <span className="text-slate-800">{a.preferred_time || "-"}</span></p>
                          <p className="text-slate-500">Service: <span className="text-slate-800">{a.service || "-"}</span></p>
                        </div>
                        {a.notes && (
                          <p className="text-sm text-slate-700 mt-3 leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-xl p-3 border border-slate-100">
                            {a.notes}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-3">Submitted {fmtDate(a.created_at)}</p>
                      </div>
                      <div className="flex gap-2 self-start flex-wrap">
                        {["pending", "confirmed", "completed", "cancelled"].map((s) => (
                          <button
                            key={s}
                            data-testid={`set-status-${a.id}-${s}`}
                            onClick={() => setApptStatus(a, s)}
                            className={`px-3 h-8 rounded-full border text-xs font-medium capitalize transition-colors ${
                              a.status === s
                                ? "bg-[#0A2540] text-white border-[#0A2540]"
                                : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                            }`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-3xl bg-white border border-slate-100 p-6 flex items-center justify-between shadow-[0_8px_30px_rgba(10,37,64,0.04)]">
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</p>
      <p className="font-heading text-3xl font-medium text-slate-900 mt-2">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{sub}</p>
    </div>
    <div className="w-12 h-12 rounded-2xl bg-[#0A2540] text-white flex items-center justify-center">
      <Icon size={20} strokeWidth={1.5} />
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const styles = {
    pending: "text-amber-700 bg-amber-50 border-amber-200",
    confirmed: "text-sky-700 bg-sky-50 border-sky-200",
    completed: "text-emerald-700 bg-emerald-50 border-emerald-200",
    cancelled: "text-slate-600 bg-slate-100 border-slate-200",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.18em] font-semibold border px-2 py-0.5 rounded-full ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
};

const EmptyState = ({ label }) => (
  <div className="text-center py-16 text-slate-500 text-sm">{label}</div>
);

const isLast7 = (item) => {
  if (!item?.created_at) return false;
  try {
    const d = new Date(item.created_at);
    return Date.now() - d.getTime() < 7 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};

export default AdminDashboard;
